import { handleActions } from 'redux-actions';
import { SubmissionError } from 'redux-form';

import reduce from 'lodash/reduce';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';

import http from '../../../shared/http';
import endpoints from '../../../../config/endpoints';

import { enum as popupTypes } from '../../../../../../../enums/popup-types';
import { enum as languages } from '../../../../../../../enums/languages';

import { patterns } from '../../../shared/form/validation';

export const namespace = 'popupsPopupEditor';

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const SET_INITIAL_VALUES = `${namespace}/set-initial-values`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const CHANGE_LANGUAGE = `${namespace}/change-language`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;

export function isRequiredContentLanguage(language) {
	return includes([languages.en, languages.ru], language);
}

// initial state
const initialState = {
	data: null,
	isLoading: true,
	isError: false,
	language: languages.en
};

const initialFormValues = {
	campaigns: [],
	type: [],
	content: {},
	status: []
};

function mapResponseDataToStateData(data, { popupCampaigns, popupCustomContentIds, popupStatuses }) {
	const campaigns = data.campaign_ids.map((campaign_id) => {
		const campaign = find(popupCampaigns, { id: campaign_id });
		return { value: campaign.id, label: campaign.text };
	});
	const content = find(popupCustomContentIds, { id: data.content.id });
	const status = find(popupStatuses, { id: data.status });

	return {
		campaigns,
		type: [{ value: data.type, label: data.type }],
		content: data.type === popupTypes.custom
			? { id: [{ value: content.id, label: content.text }] }
			: data.content,
		status: [{ value: status.id, label: status.text }]
	};
}

function getCreatePopupRequest(data) {
	return http({
		url: endpoints.CUSTOM_POPUPS.BASE,
		method: 'post',
		actions: [SET_INITIAL_VALUES, FETCH_DATA_ERROR],
		data
	});
}

function getUpdatePopupRequest(id, data, lists) {
	return http({
		url: `${endpoints.CUSTOM_POPUPS.BASE}/${id}`,
		method: 'put',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		data,
		addToResponse: {
			popupCampaigns: lists['popup-campaigns'],
			popupCustomContentIds: lists['popup-custom-content-ids'],
			popupStatuses: lists['popup-statuses']
		}
	});
}

// actions
export const actions = {
	own: {
		fetchData: (props) => (dispatch, getState) => {
			const popupId = props.params.id;

			if (!popupId) {
				return dispatch({ type: SET_INITIAL_VALUES });
			}

			const lists = getState().root.lists;

			const request = http({
				url: `${endpoints.CUSTOM_POPUPS.BASE}/${popupId}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
				addToResponse: {
					popupCampaigns: lists['popup-campaigns'],
					popupCustomContentIds: lists['popup-custom-content-ids'],
					popupStatuses: lists['popup-statuses']
				}
			});

			return request(dispatch);
		},
		onSubmit: (values, _, props) => (dispatch, getState) => {
			const type = values.type[0].value;

			if (type === popupTypes.simple) {
				validateSimpleContent(values.content);
			}

			const popupId = props.params.id;

			const payload = {
				campaign_ids: values.campaigns.map((c) => c.value),
				type,
				content: type === popupTypes.custom
					? { id: values.content.id[0].value }
					: normalizeSimpleContent(values.content),
				status: values.status[0].value
			};

			dispatch(actions.own.toggleProgressBar());

			const request = popupId
				? getUpdatePopupRequest(popupId, payload, getState().root.lists)
				: getCreatePopupRequest(payload);

			return request(dispatch);
		},
		onChangeLang: (lang) => (dispatch, getState) => {
			dispatch({ type: CHANGE_LANGUAGE, lang });
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR })
	}
};

const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: mapResponseDataToStateData(action.payload.data, action.meta),
			isLoading: false,
			isError: false
		}),
		[SET_INITIAL_VALUES]: (state, action) => ({
			...state,
			data: initialFormValues,
			isLoading: false,
			isError: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			isLoading: false,
			isError: true
		}),
		[CHANGE_LANGUAGE]: (state, action) => ({
			...state,
			language: action.lang
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		})
	}
);

// reducers
export default handleActions(reducers, initialState);

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getPopupCampaigns: state => state.root.lists['popup-campaigns'],
	getPopupTypes: state => state.root.lists['popup-types'],
	getPopupCustomContentIds: state => state.root.lists['popup-custom-content-ids'],
	getPopupStatuses: state => state.root.lists['popup-statuses'],
	getTabLangs: (state) => {
		const languages = state.root.lists.languages;
		const currentLang = state[namespace].language;

		return languages.map((lang) => ({
			id: lang.id,
			text: lang.text,
			isSelected: true,
			isActive: lang.id === currentLang,
			isDefault: lang.id === currentLang
		}));
	}
};

function validateSimpleContent(content) {
	forEach(content, (info, lang) => {
		const { image, link } = content[lang];

		if (!image) {
			throw new SubmissionError({
				content: {
					[lang]: {
						image: 'required'
					}
				},
				_error: `Image link is required for ${lang.toUpperCase()}`
			});
		}

		if (!patterns.url.test(image)) {
			throw new SubmissionError({
				content: {
					[lang]: {
						image: 'invalid url'
					}
				},
				_error: `Image link is invalid for ${lang.toUpperCase()}`
			});
		}

		if (!patterns.extensionsOf.images.test(image)) {
			throw new SubmissionError({
				content: {
					[lang]: {
						image: 'invalid extension'
					}
				},
				_error: `Image extension is invalid for ${lang.toUpperCase()}`
			});
		}

		if (link && !patterns.url.test(link)) {
			throw new SubmissionError({
				content: {
					[lang]: {
						link: 'invalid url'
					}
				},
				_error: `Link is invalid for ${lang.toUpperCase()}`
			});
		}
	});

	if (!content[languages.en]) {
		throw new SubmissionError({
			content: {
				[languages.en]: {
					image: 'required'
				}
			},
			_error: `Content is required for ${languages.en.toUpperCase()}`
		});
	}

	if (!content[languages.ru]) {
		throw new SubmissionError({
			content: {
				[languages.ru]: {
					image: 'required'
				}
			},
			_error: `Content is required for ${languages.ru.toUpperCase()}`
		});
	}
}

function normalizeSimpleContent(content) {
	return reduce(content, (content, value, lang) => ({
		...content,
		[lang]: {
			image: value.image,
			link: value.link || null
		}
	}), {});
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/popups/popup/editor/index.js